/**
 * Step 2 — Professional identity.
 * Captures: full_name, bar_council_id (optional), years_experience,
 * primary_court. Pre-fills full_name from Google display_name.
 */
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { useSignupTelemetry } from '../useSignupTelemetry';
import {
  ProfessionalIdentityStepPayload,
  YearsExperienceBucket,
  PrimaryCourt,
} from '../signupApi';

interface Props {
  onAdvance: () => void;
  onBack: () => void;
}

const YEARS_OPTIONS: { value: YearsExperienceBucket; label: string }[] = [
  { value: '0-2',   label: 'Less than 3 years' },
  { value: '3-5',   label: '3 to 5 years' },
  { value: '6-10',  label: '6 to 10 years' },
  { value: '11-20', label: '11 to 20 years' },
  { value: '20+',   label: '20+ years' },
];

const COURT_OPTIONS: { value: PrimaryCourt; label: string }[] = [
  { value: 'district',      label: 'District / Subordinate court' },
  { value: 'high_court',    label: 'High Court' },
  { value: 'supreme_court', label: 'Supreme Court' },
  { value: 'tribunal',      label: 'Tribunals' },
  { value: 'none_yet',      label: 'Not actively appearing yet' },
];

const ProfessionalIdentityStep: React.FC<Props> = ({ onAdvance, onBack }) => {
  const authUser = useSelector((s: RootState) => s.auth.user);
  const [fullName, setFullName] = useState<string>(authUser?.displayName ?? '');
  const [barCouncilId, setBarCouncilId] = useState<string>('');
  const [years, setYears] = useState<YearsExperienceBucket | ''>('');
  const [court, setCourt] = useState<PrimaryCourt | ''>('');
  const { complete, skip, pending, error } = useSignupTelemetry({
    stepName: 'professional_identity',
    onAdvance,
  });

  const canSubmit = fullName.trim().length > 0 && years && court;

  const submit = () => {
    if (!canSubmit) return;
    const payload: ProfessionalIdentityStepPayload = {
      full_name: fullName.trim(),
      bar_council_id: barCouncilId.trim() || null,
      years_experience: years as YearsExperienceBucket,
      primary_court: court as PrimaryCourt,
    };
    complete(payload as unknown as Record<string, unknown>);
  };

  return (
    <div className="bg-surface-elevated rounded-xl border border-border-primary p-6 shadow-elevation-1">
      <div className="space-y-5">
        <Field label="Full name" required>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="As you'd like it to appear on documents"
            className="w-full rounded-lg border border-border-primary bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-legal-green-500"
          />
        </Field>

        <Field label="Bar Council registration number" hint="Optional. Format: STATE/NUMBER/YEAR (e.g. MAH/12345/2018)">
          <input
            type="text"
            value={barCouncilId}
            onChange={(e) => setBarCouncilId(e.target.value)}
            placeholder="MAH/12345/2018"
            className="w-full rounded-lg border border-border-primary bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-legal-green-500"
          />
        </Field>

        <Field label="Years of experience" required>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {YEARS_OPTIONS.map((opt) => (
              <Pill
                key={opt.value}
                selected={years === opt.value}
                onClick={() => setYears(opt.value)}
                label={opt.label}
              />
            ))}
          </div>
        </Field>

        <Field label="Primary court" required>
          <div className="space-y-2">
            {COURT_OPTIONS.map((opt) => (
              <Radio
                key={opt.value}
                checked={court === opt.value}
                onChange={() => setCourt(opt.value)}
                label={opt.label}
              />
            ))}
          </div>
        </Field>

        {error && (
          <div className="p-3 bg-crimson-50 dark:bg-crimson-900/20 border border-crimson-200 dark:border-crimson-800 rounded-lg text-crimson-700 dark:text-crimson-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <button
            onClick={skip}
            disabled={pending}
            className="text-sm text-text-tertiary hover:text-text-secondary disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || pending}
            className="px-6 py-2.5 bg-legal-green-500 text-white font-semibold rounded-lg hover:bg-legal-green-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm shadow-elevation-1"
          >
            {pending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </span>
            ) : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---- shared mini-components ----

const Field: React.FC<{ label: string; required?: boolean; hint?: string; children: React.ReactNode }> = ({
  label, required, hint, children,
}) => (
  <div>
    <label className="block text-sm font-medium text-text-primary mb-1.5">
      {label} {required && <span className="text-crimson-500">*</span>}
    </label>
    {hint && <p className="text-xs text-text-tertiary mb-2">{hint}</p>}
    {children}
  </div>
);

const Pill: React.FC<{ selected: boolean; onClick: () => void; label: string }> = ({
  selected, onClick, label,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg border px-3 py-2 text-xs transition-colors ${
      selected
        ? 'border-legal-green-500 bg-legal-green-50 dark:bg-legal-green-900/20 text-legal-green-700 dark:text-legal-green-300'
        : 'border-border-primary bg-surface text-text-secondary hover:border-legal-green-400/50'
    }`}
  >
    {label}
  </button>
);

const Radio: React.FC<{ checked: boolean; onChange: () => void; label: string }> = ({
  checked, onChange, label,
}) => (
  <label className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
    checked
      ? 'border-legal-green-500 bg-legal-green-50 dark:bg-legal-green-900/20'
      : 'border-border-primary bg-surface hover:border-legal-green-400/50'
  }`}>
    <input
      type="radio"
      checked={checked}
      onChange={onChange}
      className="accent-legal-green-500"
    />
    <span className="text-sm text-text-primary">{label}</span>
  </label>
);

export default ProfessionalIdentityStep;
